import { useState, type FC } from "react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import SessionStorageHelper from "../../utils/sessionStorageHelper";
import { cn } from "../../lib/utils";

const TargetWeight: FC = () => {
  const navigate = useNavigate();
  const [unit, setUnit] = useState<"Lbs" | "Kg">("Lbs");
  const [weight, setWeight] = useState<string | number>("");

  const isWeightValid = () => {
    const weightNum = Number(weight);
    const maxWeight = unit === "Lbs" ? 1000 : 500;

    // Allow any positive weight up to maximum (no minimum restriction)
    return weight !== "" && weightNum > 0 && weightNum <= maxWeight;
  };

  const handleSkip = () => {
    SessionStorageHelper.saveMultipleFields({
      target_weight_in: null as any,
      target_weight_kg: null as any,
      target_weight_lbs: null as any,
    });
    navigate("/glucera" + GluceraNavigationRoutes.MAIN_REASON_WEIGHT_LOSS);
  };

  const handleNext = () => {
    if (!isWeightValid()) {
      return;
    }

    const saveData: any = {};

    // Clear both, then set only selected unit
    saveData.target_weight_in = null;
    saveData.target_weight_kg = null;
    saveData.target_weight_lbs = null;

    if (unit === "Kg") {
      saveData.target_weight_in = "kg";
      saveData.target_weight_kg = Number(weight);
    } else {
      saveData.target_weight_in = "lbs";
      saveData.target_weight_lbs = Number(weight);
    }

    SessionStorageHelper.saveMultipleFields(saveData);
    navigate("/glucera" + GluceraNavigationRoutes.MAIN_REASON_WEIGHT_LOSS);
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 p-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-muted-foreground">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 7 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col px-6 py-2">
          <div className="mb-3 space-y-2">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
              What's Your Target Weight?
            </h1>
            <p className="text-sm text-muted-foreground">
              Select unit and enter your target weight
            </p>
          </div>

          {/* Weight Unit Tabs */}
          <div className="mb-4 flex gap-2">
            <button
              className={cn(
                "flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all",
                unit === "Lbs"
                  ? "bg-muted text-foreground"
                  : "bg-card text-muted-foreground hover:bg-muted"
              )}
              onClick={() => {
                setUnit("Lbs");
                setWeight("");
              }}
            >
              Lbs
            </button>
            <button
              className={cn(
                "flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all",
                unit === "Kg"
                  ? "bg-muted text-foreground"
                  : "bg-card text-muted-foreground hover:bg-muted"
              )}
              onClick={() => {
                setUnit("Kg");
                setWeight("");
              }}
            >
              Kg
            </button>
          </div>

          {/* Weight Input */}
          <div className="mb-6">
            <Label className="mb-2 block text-base font-semibold text-foreground">
              Target Weight
            </Label>
            <input
              type="number"
              value={weight}
              onChange={e => setWeight(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder={unit}
            />

            {/* Dynamic Helper Text */}
            {(() => {
              const weightNum = Number(weight);
              const maxWeight = unit === "Lbs" ? 1000 : 500;

              // Show info message for very low weights (but still allow them)
              if (weight !== "" && weightNum > 0 && weightNum < 25) {
                return (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/50 dark:bg-blue-900/20">
                    <p className="mb-2 text-sm font-semibold text-blue-800 dark:text-blue-400">
                      💡 Small weight loss goal detected
                    </p>
                    <p className="mb-2 text-xs text-blue-700 dark:text-blue-300">
                      You've set a target weight of {weightNum}{" "}
                      {unit.toLowerCase()}. This is perfect for small,
                      achievable goals!
                    </p>
                    <div className="space-y-1 text-xs text-blue-600 dark:text-blue-400">
                      <p className="font-medium">✓ Tips for success:</p>
                      <ul className="ml-4 list-disc space-y-0.5">
                        <li>Small goals are easier to achieve and maintain</li>
                        <li>Focus on consistent healthy habits</li>
                        <li>Track your progress regularly</li>
                        <li>Celebrate small wins along the way</li>
                      </ul>
                    </div>
                  </div>
                );
              }

              // Show warning if weight is above maximum
              if (weight !== "" && weightNum > maxWeight) {
                return (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-900/20">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-400">
                      ❌ Target weight exceeds maximum limit
                    </p>
                    <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                      Please enter a value between 1 - {maxWeight}{" "}
                      {unit.toLowerCase()}.
                    </p>
                  </div>
                );
              }

              // Show success if weight is valid (25+)
              if (weight !== "" && weightNum >= 25 && weightNum <= maxWeight) {
                return (
                  <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-2 dark:border-green-900/50 dark:bg-green-900/20">
                    <p className="text-xs font-medium text-green-800 dark:text-green-400">
                      ✓ Great! This is a healthy target weight.
                    </p>
                  </div>
                );
              }

              // Default helper text
              return (
                <p className="mt-2 text-xs text-muted-foreground">
                  {unit === "Lbs"
                    ? "Enter any target weight (1 - 1000 lbs)"
                    : "Enter any target weight (1 - 500 kg)"}
                </p>
              );
            })()}
          </div>

          {/* Buttons */}
          <div className="mt-auto space-y-3 pt-2">
            <Button
              onClick={handleNext}
              disabled={!isWeightValid()}
              className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
            >
              Next
            </Button>

            {/* Helper text when button is disabled */}
            {!isWeightValid() && weight !== "" && (
              <p className="text-center text-xs text-muted-foreground">
                Please enter a valid weight to continue
              </p>
            )}

            <Button
              variant="ghost"
              onClick={handleSkip}
              className="h-11 w-full rounded-xl text-sm sm:h-12 sm:text-base"
            >
              Skip
            </Button>
          </div>
        </div>

        {/* Safe area padding */}
        <div className="pb-safe p-2" />
      </div>
    </div>
  );
};

export default TargetWeight;
